import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatMoney, formatOrderMoney, formatApiError } from "../../lib/api";
import { ChevronDown, ChevronRight, Search, Package, X, Mail, Trash2, Download, FileDown, Pencil, Save, RotateCcw, Plus, Send, Upload } from "lucide-react";
import { toast } from "sonner";
import AdminNewOrder from "./AdminNewOrder";
import OrderFullEditModal from "./OrderFullEditModal";

const STATUS_HU = {
  feldolgozas_alatt: { label: "Feldolgozás alatt", color: "#C58800", bg: "#FFF4E0" },
  reszben_megjott: { label: "Részben megjött", color: "#2680C2", bg: "#E8F0FA" },
  teljesitve: { label: "Teljesítve", color: "#0A8043", bg: "#E8F8E8" },
};

const STATUS_KEYS = ["feldolgozas_alatt", "reszben_megjott", "teljesitve"];

// Legacy → current status mapping. Old orders may still be returned with
// earlier status keys until startup migration finishes, so we normalise
// at the UI level too.
const LEGACY_STATUS_MAP = {
  // Original 3-step
  feldolgozatlan: "feldolgozas_alatt",
  kesz: "reszben_megjott",
  specialis: "feldolgozas_alatt",
  // Interim 4-step (Feb 2026 — atveve / visszaigazolva / szallitas_alatt / kiszamlazva)
  atveve: "feldolgozas_alatt",
  visszaigazolva: "reszben_megjott",
  szallitas_alatt: "reszben_megjott",
  kiszamlazva: "teljesitve",
  // Pre-June-2026 3-step
  feldolgozva: "reszben_megjott",
  atveheto: "teljesitve",
};
const normaliseStatus = (s) => LEGACY_STATUS_MAP[s] || s;

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState({});
  const [selected, setSelected] = useState({}); // {orderId: true}
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportColumns, setExportColumns] = useState([]); // [{key,label}]
  const [exportColumnSelection, setExportColumnSelection] = useState({}); // {key: true}
  const [exporting, setExporting] = useState(false);
  const [showNewOrder, setShowNewOrder] = useState(false);
  const [editOrder, setEditOrder] = useState(null);
  const importInputRef = useRef(null);

  // Load available columns once
  useEffect(() => {
    api.get("/admin/orders/export-columns")
      .then((r) => {
        setExportColumns(r.data.columns);
        // default: select all columns
        const sel = {};
        r.data.columns.forEach((c) => { sel[c.key] = true; });
        setExportColumnSelection(sel);
      })
      .catch(() => {});
  }, []);

  const load = () => {
    setLoading(true);
    api.get("/admin/orders").then((r) => setOrders(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const exportXls = async (orderId, dateStr) => {
    try {
      const res = await api.get(`/admin/orders/${orderId}/export-xls`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/vnd.ms-excel" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const dd = dateStr ? new Date(dateStr) : new Date();
      const fallback = `Bestellung_${String(dd.getDate()).padStart(2, "0")}.${String(dd.getMonth() + 1).padStart(2, "0")}_${orderId.slice(0, 8).toUpperCase()}.xls`;
      a.download = m ? m[1] : fallback;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const changeStatus = async (orderId, newStatus) => {
    const old = orders.find((o) => o.id === orderId)?.status;
    setOrders((arr) => arr.map((o) => (o.id === orderId ? { ...o, status: newStatus } : o)));
    try {
      const { data } = await api.patch(`/admin/orders/${orderId}`, { status: newStatus });
      const em = data?.status_email;
      if (em?.sent && em?.recipients?.length) {
        toast.success(`Státusz frissítve · Email kiküldve: ${em.recipients.join(", ")}`);
      } else if (em && em.error === "smtp_not_configured") {
        toast.success("Státusz frissítve (email-küldés nincs konfigurálva)");
      } else if (em && em.sent === false) {
        toast.warning(`Státusz frissítve, de az email nem ment ki: ${em.error || "ismeretlen hiba"}`);
      } else {
        toast.success("Státusz frissítve");
      }
    } catch (e) {
      setOrders((arr) => arr.map((o) => (o.id === orderId ? { ...o, status: old } : o)));
      toast.error(formatApiError(e));
    }
  };

  const deleteOrder = async (orderId) => {
    if (!window.confirm(`Biztosan törli a #${orderId.slice(0, 8).toUpperCase()} rendelést?\n\nA művelet nem vonható vissza.`)) return;
    try {
      await api.delete(`/admin/orders/${orderId}`);
      toast.success("Rendelés törölve");
      setOrders((arr) => arr.filter((o) => o.id !== orderId));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const exportJson = async () => {
    try {
      const res = await api.get("/admin/orders/export-json", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : `orders_export_${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Rendelések exportálva (JSON)");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const importJson = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    const mode = window.prompt(
      "Import mód:\n\n1 = Egyesítés (meglévő frissítése + új hozzáadása)\n2 = Csak új rendelések\n3 = MINDENT TÖRÖL, majd feltölt\n\nÍrd be a szám: 1, 2 vagy 3",
      "1"
    );
    if (mode === null) return;
    const m = mode.trim();
    if (!["1", "2", "3"].includes(m)) { toast.error("Érvénytelen mód. 1, 2 vagy 3 lehet."); return; }
    if (m === "3" && !window.confirm("MÓD 3: az ÖSSZES jelenlegi rendelés törlődik, mielőtt a fájl feltöltésre kerül.\n\nBiztosan folytatod?")) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("mode", m);
    try {
      const { data } = await api.post("/admin/orders/import-json", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Import kész · új: ${data.inserted} · frissítve: ${data.updated} · kihagyva: ${data.skipped}${data.deleted ? ` · törölve: ${data.deleted}` : ""}${data.errors ? ` · hiba: ${data.errors}` : ""}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return orders;
    return orders.filter((o) => {
      const blob = [
        o.id,
        o.po_number || "",
        o.user_email || "",
        o.user_company || "",
        ...(o.items || []).map((i) => `${i.sku} ${i.name}`),
      ].join(" ").toLowerCase();
      return blob.includes(needle);
    });
  }, [orders, q]);

  const summary = useMemo(() => {
    const itemCount = filtered.reduce((s, o) => s + (o.items?.length || 0), 0);
    const total = filtered.reduce((s, o) => s + (o.total || 0), 0);
    return { count: filtered.length, items: itemCount, total };
  }, [filtered]);

  const exportAllXls = async () => {
    // Open the picker modal — user chooses which orders + columns to export
    setExportModalOpen(true);
  };

  const runExport = async () => {
    setExporting(true);
    try {
      const selectedIds = Object.entries(selected).filter(([, v]) => v).map(([id]) => id);
      const selectedCols = Object.entries(exportColumnSelection)
        .filter(([, v]) => v)
        .map(([key]) => key);
      if (selectedCols.length === 0) {
        toast.error("Válassz ki legalább egy oszlopot.");
        setExporting(false);
        return;
      }
      const res = await api.post("/admin/orders/export-xls",
        {
          order_ids: selectedIds.length > 0 ? selectedIds : null,
          columns: selectedCols,
        },
        { responseType: "blob" },
      );
      const blob = new Blob([res.data], { type: "application/vnd.ms-excel" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : `Rendelesek_${new Date().toISOString().slice(0, 10)}.xls`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(
        selectedIds.length > 0
          ? `${selectedIds.length} rendelés exportálva (${selectedCols.length} oszlop)`
          : `Összes rendelés exportálva (${selectedCols.length} oszlop)`,
      );
      setExportModalOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExporting(false);
    }
  };

  // Bulk select helpers
  const toggleSelect = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const selectedCount = Object.values(selected).filter(Boolean).length;
  const allFilteredSelected = filtered.length > 0 && filtered.every((o) => selected[o.id]);
  const toggleSelectAllFiltered = () => {
    if (allFilteredSelected) {
      // Deselect just the currently-filtered ones
      setSelected((s) => {
        const next = { ...s };
        filtered.forEach((o) => { delete next[o.id]; });
        return next;
      });
    } else {
      setSelected((s) => {
        const next = { ...s };
        filtered.forEach((o) => { next[o.id] = true; });
        return next;
      });
    }
  };

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  return (
    <div>
      <div className="flex items-end justify-between gap-3 mb-2">
        <h1 className="text-2xl font-bold uppercase tracking-wide">Rendelések</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowNewOrder((s) => !s)}
            className="btn-primary"
            data-testid="new-order-toggle"
            title="Rendelés manuális felvitele egy vevőhöz (pl. telefonos rendelés)"
          >
            <Plus size={14} /> Új rendelés felvitele
          </button>
          <button
            onClick={exportAllXls}
            className="btn-yellow"
            data-testid="export-all-orders-btn"
            title="Rendelések exportálása — oszlopok és rendelések kiválasztásával"
          >
            <FileDown size={14} /> Exportálás (.xls){selectedCount > 0 ? ` — ${selectedCount} kijelölve` : ""}
          </button>
          <button
            onClick={exportJson}
            className="btn-ghost"
            data-testid="export-json-btn"
            title="Összes rendelés letöltése JSON fájlként (mentés / átvitel másik környezetbe)"
          >
            <Download size={14} /> Export JSON
          </button>
          <button
            onClick={() => importInputRef.current?.click()}
            className="btn-ghost"
            data-testid="import-json-btn"
            title="Rendelések importálása JSON fájlból (upsert / csak új / mindent töröl+feltölt)"
          >
            <Upload size={14} /> Import JSON
          </button>
          <input
            ref={importInputRef}
            type="file"
            accept="application/json,.json"
            onChange={importJson}
            style={{ display: "none" }}
            data-testid="import-json-input"
          />
        </div>
      </div>
      <p className="text-sm mb-4" style={{ color: "var(--text-mute)" }}>
        Az összes leadott rendelés. Kattintson egy sorra a tételek megtekintéséhez.
      </p>
      <div className="border-b-2 mb-5" style={{ borderColor: "var(--brand)" }} />

      {showNewOrder && (
        <AdminNewOrder
          onCreated={(order) => { setOrders((arr) => [order, ...arr]); setShowNewOrder(false); }}
          onClose={() => setShowNewOrder(false)}
        />
      )}

      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <Stat label="Rendelés (találat)" value={summary.count.toLocaleString("hu-HU")} />
        <Stat label="Tételek (db)" value={summary.items.toLocaleString("hu-HU")} />
        <Stat label="Bruttó érték (nettó)" value={formatMoney(summary.total)} accent />
      </div>

      <div className="card-flat p-3 mb-3 flex items-center gap-2">
        <Search size={14} style={{ color: "var(--text-mute)" }} />
        <input
          className="input !py-1.5"
          placeholder="Rendelésszám, ügyfél email, cégnév, cikkszám vagy megnevezés..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="admin-orders-search"
        />
        {q && <button onClick={() => setQ("")} style={{ color: "var(--text-mute)" }}><X size={14} /></button>}
      </div>

      {loading ? (
        <div className="card-elev p-8 text-center text-sm" style={{ color: "var(--text-mute)" }}>Betöltés...</div>
      ) : filtered.length === 0 ? (
        <div className="card-elev p-12 text-center" data-testid="admin-no-orders">
          <Package size={42} className="mx-auto mb-3" style={{ color: "var(--text-disabled)" }} />
          <h3 className="font-bold text-lg mb-1">{q ? "Nincs találat" : "Még nincs rendelés"}</h3>
          <p className="text-sm" style={{ color: "var(--text-mute)" }}>
            {q ? "Próbáljon más keresési kifejezést." : "A leadott rendelések itt jelennek meg."}
          </p>
        </div>
      ) : (
        <div className="card-elev overflow-hidden" data-testid="admin-orders-list">
          <div className="section-title">Lista ({filtered.length})</div>
          <div className="overflow-x-auto bg-white">
            <table className="table-b2b">
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={(e) => { e.stopPropagation(); toggleSelectAllFiltered(); }}
                      onClick={(e) => e.stopPropagation()}
                      title={allFilteredSelected ? "Kijelölés visszavonása" : "Összes szűrt sor kijelölése"}
                      data-testid="orders-select-all"
                    />
                  </th>
                  <th style={{ width: 24 }}></th>
                  <th style={{ width: 110 }}>Rendelés</th>
                  <th>Ügyfél (e-mail)</th>
                  <th>Műhely / Cég</th>
                  <th style={{ width: 150 }}>Alvázszám (VIN)</th>
                  <th style={{ width: 70 }}>Tétel</th>
                  <th style={{ width: 130 }} className="text-right">Összesen</th>
                  <th style={{ width: 110 }}>Státusz</th>
                  <th style={{ width: 130 }}>Leadva</th>
                  <th style={{ width: 50 }}>E-mail</th>
                  <th style={{ width: 50 }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => {
                  const isOpen = !!expanded[o.id];
                  return (
                    <React.Fragment key={o.id}>
                      <tr
                        onClick={() => toggle(o.id)}
                        style={{ cursor: "pointer", background: isOpen ? "var(--surface-elev)" : "transparent" }}
                        data-testid={`admin-order-row-${o.id}`}
                      >
                        <td onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={!!selected[o.id]}
                            onChange={() => toggleSelect(o.id)}
                            data-testid={`order-select-${o.id}`}
                          />
                        </td>
                        <td>{isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                        <td><span className="font-mono font-bold" style={{ color: "var(--brand)" }}>#{o.id.slice(0, 8).toUpperCase()}</span></td>
                        <td className="font-mono text-xs">{o.user_email}</td>
                        <td className="font-bold">{o.user_company || <span style={{ color: "var(--text-disabled)" }}>—</span>}</td>
                        <td className="font-mono text-xs">{o.po_number || <span style={{ color: "var(--text-disabled)" }}>—</span>}</td>
                        <td className="font-mono">{o.items?.length || 0}</td>
                        <td className="text-right font-mono font-bold">{formatOrderMoney(o.total_display ?? o.total, o.currency)}</td>
                        <td>
                          <StatusPicker
                            order={o}
                            onChange={(s) => changeStatus(o.id, s)}
                          />
                        </td>
                        <td className="font-mono text-xs" style={{ color: "var(--text-mute)" }}>
                          {new Date(o.created_at).toLocaleString("hu-HU", { dateStyle: "short", timeStyle: "short" })}
                        </td>
                        <td>
                          {o.email_sent ? (
                            <span title={`Elküldve: ${(o.email_recipients || []).join(", ")}`}>
                              <Mail size={14} style={{ color: "var(--in-stock)" }} />
                            </span>
                          ) : (
                            <Mail size={14} style={{ color: "var(--text-disabled)" }} />
                          )}
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => setEditOrder(o)}
                              className="btn-yellow !py-1 !px-2 text-xs"
                              data-testid={`order-full-edit-${o.id}`}
                              title="Teljes körű rendelés-szerkesztés (vevő, cím, tételek, státusz)"
                            >
                              <Pencil size={12} />
                            </button>
                            <button
                              onClick={() => changeStatus(o.id, LEGACY_STATUS_MAP[o.status] || o.status)}
                              className="btn-ghost !py-1 !px-2 text-xs"
                              data-testid={`order-resend-email-${o.id}`}
                              title="Státusz e-mail (újra)küldése a vevőnek a jelenlegi státusszal"
                            >
                              <Send size={12} />
                            </button>
                            <button
                              onClick={() => exportXls(o.id, o.created_at)}
                              className="btn-ghost !py-1 !px-2 text-xs"
                              data-testid={`order-export-${o.id}`}
                              title="Bestellung XLS letöltése"
                            >
                              <Download size={12} />
                            </button>
                            <button
                              onClick={() => deleteOrder(o.id)}
                              className="btn-danger !py-1 !px-2 text-xs"
                              data-testid={`order-delete-${o.id}`}
                              title="Rendelés törlése"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr data-testid={`admin-order-expanded-${o.id}`}>
                          <td colSpan={12} style={{ background: "#FAFAFA", borderTop: "1px dashed var(--border-hi)" }}>
                            <OrderDetail
                              order={o}
                              onUpdated={(u) => setOrders((arr) => arr.map((x) => (x.id === u.id ? { ...x, ...u } : x)))}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {exportModalOpen && (
        <ExportModal
          totalOrders={orders.length}
          filteredCount={filtered.length}
          selectedCount={selectedCount}
          columns={exportColumns}
          columnSelection={exportColumnSelection}
          setColumnSelection={setExportColumnSelection}
          exporting={exporting}
          onCancel={() => setExportModalOpen(false)}
          onRun={runExport}
        />
      )}

      {editOrder && (
        <OrderFullEditModal
          order={editOrder}
          onClose={() => setEditOrder(null)}
          onSaved={(u) => {
            setOrders((arr) => arr.map((x) => (x.id === u.id ? { ...x, ...u } : x)));
            setEditOrder(null);
          }}
        />
      )}
    </div>
  );
}

function ExportModal({
  totalOrders, filteredCount, selectedCount,
  columns, columnSelection, setColumnSelection,
  exporting, onCancel, onRun,
}) {
  const allSelected = columns.length > 0 && columns.every((c) => columnSelection[c.key]);
  const toggleAll = () => {
    const next = {};
    columns.forEach((c) => { next[c.key] = !allSelected; });
    setColumnSelection(next);
  };
  const scope = selectedCount > 0
    ? `${selectedCount} kijelölt rendelés`
    : `Összes rendelés (${totalOrders})`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.55)" }}
      onClick={onCancel}
      data-testid="export-modal-backdrop"
    >
      <div
        className="bg-white rounded shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="export-modal"
      >
        <div className="section-title flex items-center justify-between">
          <span>Rendelések exportálása (.xls)</span>
          <button onClick={onCancel} className="text-white"><X size={16} /></button>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          <div className="card-flat p-3 mb-4 text-sm">
            <div className="font-bold mb-1" style={{ color: "var(--brand)" }}>Mit exportálunk?</div>
            <div data-testid="export-modal-scope">{scope}</div>
            {selectedCount === 0 && filteredCount < totalOrders && (
              <div className="text-xs mt-1" style={{ color: "var(--text-mute)" }}>
                💡 Tipp: pipálj ki konkrét rendeléseket a listából, ha csak azokat szeretnéd exportálni.
              </div>
            )}
          </div>

          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold text-sm uppercase">Oszlopok ({columns.length})</h3>
            <button
              onClick={toggleAll}
              className="text-xs underline"
              style={{ color: "var(--brand)" }}
              data-testid="export-toggle-all-cols"
            >
              {allSelected ? "Összes kikapcs." : "Összes bekapcs."}
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-4">
            {columns.map((c) => (
              <label
                key={c.key}
                className="flex items-center gap-2 text-sm cursor-pointer p-2 rounded border"
                style={{
                  borderColor: columnSelection[c.key] ? "var(--brand)" : "var(--border)",
                  background: columnSelection[c.key] ? "var(--surface-elev)" : "#fff",
                }}
                data-testid={`export-col-${c.key}`}
              >
                <input
                  type="checkbox"
                  checked={!!columnSelection[c.key]}
                  onChange={() => setColumnSelection((s) => ({ ...s, [c.key]: !s[c.key] }))}
                />
                <span>{c.label}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="border-t p-3 flex items-center justify-end gap-2" style={{ borderColor: "var(--border)" }}>
          <button onClick={onCancel} className="btn-ghost" disabled={exporting}>Mégse</button>
          <button
            onClick={onRun}
            className="btn-yellow"
            disabled={exporting}
            data-testid="export-modal-run"
          >
            {exporting ? "Generálás..." : (<><FileDown size={14} /> Exportálás</>)}
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="card-elev p-3">
      <div className="text-[10px] uppercase font-bold mb-1" style={{ color: "var(--text-mute)" }}>{label}</div>
      <div className={`font-mono ${accent ? "text-2xl font-bold" : "text-xl"}`} style={accent ? { color: "var(--brand)" } : {}}>
        {value}
      </div>
    </div>
  );
}

function StatusPicker({ order, onChange }) {
  const norm = normaliseStatus(order.status);
  const current = STATUS_HU[norm] || STATUS_HU.feldolgozas_alatt;
  return (
    <div className="flex flex-col gap-1.5" onClick={(e) => e.stopPropagation()}>
      <span
        className="text-[10px] uppercase px-1.5 py-0.5 rounded font-bold text-center"
        style={{ background: current.bg, color: current.color, border: `1px solid ${current.color}` }}
        data-testid={`order-status-current-${order.id}`}
      >
        {current.label}
      </span>
      <select
        // Always-call onChange. We treat picking the same status as a manual
        // re-send signal so the admin doesn't have to bounce through a
        // different value first (used to be required because the previous
        // implementation early-returned on value unchanged).
        value={norm}
        onChange={(e) => onChange(e.target.value)}
        className="text-[10px] uppercase font-bold px-1.5 py-1 rounded border bg-white cursor-pointer"
        style={{ borderColor: "var(--border)", color: "var(--text)" }}
        data-testid={`order-status-select-${order.id}`}
        title="Státusz módosítása — minden választás kiküldi az e-mailt"
      >
        {STATUS_KEYS.map((s) => (
          <option key={s} value={s} data-testid={`order-status-option-${order.id}-${s}`}>
            {STATUS_HU[s].label}
          </option>
        ))}
      </select>
    </div>
  );
}

function OrderDetail({ order, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState([]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const toLocalDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  const [editingDate, setEditingDate] = useState(false);
  const [dateDraft, setDateDraft] = useState(toLocalDate(order.created_at));
  const [savingDate, setSavingDate] = useState(false);

  const saveDate = async () => {
    if (!dateDraft) { toast.error("Adj meg egy dátumot."); return; }
    setSavingDate(true);
    try {
      const { data } = await api.patch(`/admin/orders/${order.id}/date`, { created_at: dateDraft });
      onUpdated(data);
      setEditingDate(false);
      toast.success("Rendelés dátuma módosítva");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingDate(false);
    }
  };

  const startEdit = () => {
    setDraft(order.items.map((it, idx) => ({ ...it, _idx: idx, _removed: false })));
    setNote("");
    setEditing(true);
  };

  const setQty = (i, q) => {
    const n = Math.max(1, parseInt(q, 10) || 1);
    setDraft((d) => d.map((it, j) => (j === i ? { ...it, quantity: n } : it)));
  };
  const toggleRemove = (i) =>
    setDraft((d) => d.map((it, j) => (j === i ? { ...it, _removed: !it._removed } : it)));

  const unitDisp = (it) => it.unit_price_display ?? it.unit_price;
  const keptItems = draft.filter((it) => !it._removed);
  const previewTotal = keptItems.reduce((s, it) => s + unitDisp(it) * it.quantity, 0);

  const save = async () => {
    if (keptItems.length === 0) {
      toast.error("Legalább egy tételnek maradnia kell a rendelésben.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/orders/${order.id}/items`, {
        items: keptItems.map((it) => ({ index: it._idx, quantity: it.quantity })),
        note,
      });
      const em = data?.modify_email;
      if (em?.sent && em?.recipients?.length) {
        toast.success(`Rendelés módosítva · E-mail kiküldve: ${em.recipients.join(", ")}`);
      } else if (em && em.error === "smtp_not_configured") {
        toast.success("Rendelés módosítva (email-küldés nincs konfigurálva)");
      } else if (em && em.sent === false) {
        toast.warning(`Rendelés módosítva, de az e-mail nem ment ki: ${em.error || "ismeretlen hiba"}`);
      } else {
        toast.success("Rendelés módosítva");
      }
      onUpdated(data);
      setEditing(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleArrived = async (idx) => {
    const newItems = order.items.map((it, j) => (j === idx ? { ...it, arrived: !it.arrived } : it));
    const arrivedIndexes = newItems.map((it, j) => (it.arrived ? j : -1)).filter((j) => j >= 0);
    onUpdated({ ...order, items: newItems }); // optimistic
    try {
      const { data } = await api.patch(`/admin/orders/${order.id}/arrivals`, { arrived_indexes: arrivedIndexes });
      onUpdated(data);
    } catch (e) {
      onUpdated(order); // revert
      toast.error(formatApiError(e));
    }
  };

  const arrivedCount = order.items.filter((it) => it.arrived).length;

  return (
    <div className="p-4">
      {order.notes && (
        <div className="mb-3 text-xs p-2" style={{ background: "#FFF8E1", border: "1px solid #F4D88F", borderRadius: 3 }}>
          <strong>Megjegyzés:</strong> {order.notes}
        </div>
      )}

      {!editing && (
        <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-xs" data-testid={`order-date-block-${order.id}`}>
            <span className="font-bold uppercase" style={{ color: "var(--text-mute)" }}>Dátum:</span>
            {editingDate ? (
              <>
                <input
                  type="date"
                  className="input !py-1 !px-2 text-xs"
                  style={{ width: 150 }}
                  value={dateDraft}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setDateDraft(e.target.value)}
                  data-testid={`order-date-input-${order.id}`}
                />
                <button onClick={saveDate} disabled={savingDate} className="btn-yellow !py-1 !px-2 text-xs"
                  data-testid={`order-date-save-${order.id}`}>
                  <Save size={12} /> {savingDate ? "Mentés..." : "Mentés"}
                </button>
                <button onClick={() => { setEditingDate(false); setDateDraft(toLocalDate(order.created_at)); }}
                  className="btn-ghost !py-1 !px-2 text-xs">Mégse</button>
              </>
            ) : (
              <>
                <span className="font-mono">
                  {new Date(order.created_at).toLocaleString("hu-HU", { dateStyle: "short", timeStyle: "short" })}
                </span>
                <button onClick={() => { setDateDraft(toLocalDate(order.created_at)); setEditingDate(true); }}
                  className="btn-ghost !py-1 !px-2 text-xs" data-testid={`order-date-edit-${order.id}`}
                  title="Rendelés dátumának módosítása (akár régebbi dátumra)">
                  <Pencil size={11} /> Dátum módosítása
                </button>
              </>
            )}
          </div>
          <button
            onClick={startEdit}
            className="btn-ghost !py-1.5 !px-3 text-xs"
            data-testid={`order-edit-btn-${order.id}`}
            title="Tételek szerkesztése (darabszám módosítás, tétel törlés)"
          >
            <Pencil size={12} /> Tételek szerkesztése
          </button>
        </div>
      )}

      {editing ? (
        <div data-testid={`order-edit-panel-${order.id}`}>
          <table className="table-b2b" style={{ background: "#fff", border: "1px solid var(--border)" }}>
            <thead>
              <tr>
                <th style={{ width: 140 }}>Cikkszám</th>
                <th>Megnevezés</th>
                <th style={{ width: 130 }}>Db</th>
                <th style={{ width: 110 }} className="text-right">Egységár</th>
                <th style={{ width: 130 }} className="text-right">Tétel</th>
                <th style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {draft.map((it, idx) => (
                <tr key={idx} style={it._removed ? { opacity: 0.45, textDecoration: "line-through", background: "#FDECEC" } : {}}>
                  <td><span className="font-mono text-xs font-bold" style={{ color: "var(--brand)" }}>{it.sku}</span></td>
                  <td>{it.name}</td>
                  <td>
                    <div className="flex items-center gap-1">
                      <button
                        className="btn-ghost !py-0.5 !px-1.5 text-xs"
                        disabled={it._removed || it.quantity <= 1}
                        onClick={() => setQty(idx, it.quantity - 1)}
                        data-testid={`order-edit-qty-dec-${order.id}-${idx}`}
                      >−</button>
                      <input
                        type="number"
                        min={1}
                        value={it.quantity}
                        disabled={it._removed}
                        onChange={(e) => setQty(idx, e.target.value)}
                        className="input !py-0.5 !px-1 text-center font-mono"
                        style={{ width: 52 }}
                        data-testid={`order-edit-qty-${order.id}-${idx}`}
                      />
                      <button
                        className="btn-ghost !py-0.5 !px-1.5 text-xs"
                        disabled={it._removed}
                        onClick={() => setQty(idx, it.quantity + 1)}
                        data-testid={`order-edit-qty-inc-${order.id}-${idx}`}
                      >+</button>
                    </div>
                  </td>
                  <td className="text-right font-mono">{formatOrderMoney(unitDisp(it), order.currency)}</td>
                  <td className="text-right font-mono font-bold">
                    {it._removed ? "—" : formatOrderMoney(unitDisp(it) * it.quantity, order.currency)}
                  </td>
                  <td>
                    <button
                      onClick={() => toggleRemove(idx)}
                      className={`${it._removed ? "btn-ghost" : "btn-danger"} !py-1 !px-2 text-xs`}
                      title={it._removed ? "Tétel visszaállítása" : "Tétel törlése a rendelésből"}
                      data-testid={`order-edit-remove-${order.id}-${idx}`}
                    >
                      {it._removed ? <RotateCcw size={12} /> : <Trash2 size={12} />}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4} className="text-right font-bold uppercase" style={{ background: "var(--surface-elev)" }}>Új végösszeg (nettó):</td>
                <td className="text-right font-mono font-bold text-base" style={{ background: "var(--surface-elev)", color: "var(--brand)" }} data-testid={`order-edit-preview-total-${order.id}`}>
                  {formatOrderMoney(order.currency === "HUF" ? Math.round(previewTotal) : previewTotal, order.currency)}
                </td>
                <td style={{ background: "var(--surface-elev)" }}></td>
              </tr>
            </tfoot>
          </table>

          <div className="mt-3">
            <label className="text-xs font-bold uppercase block mb-1" style={{ color: "var(--text-mute)" }}>
              Megjegyzés a vevőnek (az e-mailben is megjelenik)
            </label>
            <textarea
              className="input"
              rows={2}
              placeholder="Pl.: A 06A115561B szűrő sajnos nem szállítható, ezért töröltük a rendelésből..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid={`order-edit-note-${order.id}`}
            />
          </div>

          <div className="mt-3 flex items-center justify-end gap-2">
            <button onClick={() => setEditing(false)} className="btn-ghost text-xs" disabled={saving} data-testid={`order-edit-cancel-${order.id}`}>
              Mégse
            </button>
            <button onClick={save} className="btn-yellow text-xs" disabled={saving} data-testid={`order-edit-save-${order.id}`}>
              <Save size={12} /> {saving ? "Mentés..." : "Mentés és e-mail küldése"}
            </button>
          </div>
        </div>
      ) : (
        <table className="table-b2b" style={{ background: "#fff", border: "1px solid var(--border)" }}>
          <thead>
            <tr>
              <th style={{ width: 110 }} title="Pipáld ki, ha a tétel megérkezett — a 'Részben megjött' státusz e-mail tartalmazza a bontást">
                Megérkezett{arrivedCount > 0 ? ` (${arrivedCount}/${order.items.length})` : ""}
              </th>
              <th style={{ width: 140 }}>Cikkszám</th>
              <th>Megnevezés</th>
              <th style={{ width: 60 }}>Db</th>
              <th style={{ width: 110 }} className="text-right">Egységár</th>
              <th style={{ width: 130 }} className="text-right">Tétel</th>
            </tr>
          </thead>
          <tbody>
            {order.items.map((it, idx) => (
              <tr key={idx} style={it.arrived ? { background: "#F2FBF2" } : {}}>
                <td>
                  <label className="flex items-center gap-1.5 cursor-pointer text-xs">
                    <input
                      type="checkbox"
                      checked={!!it.arrived}
                      onChange={() => toggleArrived(idx)}
                      data-testid={`order-arrived-${order.id}-${idx}`}
                    />
                    {it.arrived ? (
                      <span className="font-bold" style={{ color: "var(--in-stock, #0A8043)" }}>Megjött</span>
                    ) : (
                      <span style={{ color: "var(--text-mute)" }}>Várjuk</span>
                    )}
                  </label>
                </td>
                <td><span className="font-mono text-xs font-bold" style={{ color: "var(--brand)" }}>{it.sku}</span></td>
                <td>{it.name}</td>
                <td className="font-mono">{it.quantity}</td>
                <td className="text-right font-mono">{formatOrderMoney(it.unit_price_display ?? it.unit_price, order.currency)}</td>
                <td className="text-right font-mono font-bold">{formatOrderMoney(it.line_total_display ?? it.line_total, order.currency)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={5} className="text-right font-bold uppercase" style={{ background: "var(--surface-elev)" }}>Összesen (nettó):</td>
              <td className="text-right font-mono font-bold text-base" style={{ background: "var(--surface-elev)", color: "var(--brand)" }}>
                {formatOrderMoney(order.total_display ?? order.total, order.currency)}
              </td>
            </tr>
          </tfoot>
        </table>
      )}

      {!editing && arrivedCount > 0 && arrivedCount < order.items.length && (
        <div className="mt-2 text-[11px]" style={{ color: "var(--text-mute)" }} data-testid={`order-arrival-hint-${order.id}`}>
          💡 Tipp: állítsd a státuszt „Részben megjött"-re (akár újra kiválasztva) — az e-mail tartalmazni fogja, mely tételek érkeztek meg és melyekre vár még a vevő.
        </div>
      )}

      {!editing && order.modifications?.length > 0 && (
        <div className="mt-3 text-xs p-2" style={{ background: "#F3F7FB", border: "1px solid #B9D2EA", borderRadius: 3 }} data-testid={`order-modifications-${order.id}`}>
          <strong style={{ color: "var(--brand)" }}>Módosítási előzmények:</strong>
          <ul className="mt-1 space-y-1" style={{ paddingLeft: 16, listStyle: "disc" }}>
            {order.modifications.map((m, i) => (
              <li key={i}>
                <span className="font-mono" style={{ color: "var(--text-mute)" }}>
                  {new Date(m.at).toLocaleString("hu-HU", { dateStyle: "short", timeStyle: "short" })}
                </span>
                {m.by && <> · <strong style={{ color: "var(--brand)" }}>{m.by}</strong></>}
                {m.changes?.length > 0 && <> — {m.changes.join("; ")}</>}
                {m.note && <> · <em>{m.note}</em></>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {order.email_recipients?.length > 0 && (
        <div className="mt-2 text-[11px]" style={{ color: "var(--text-mute)" }}>
          ✉ Visszaigazoló email elküldve: <strong>{order.email_recipients.join(", ")}</strong>
        </div>
      )}
    </div>
  );
}
